import { View, TableView, Dialog } from 'web-mojo';
import { FirmwareBuild, FirmwareBuildHistoryList } from '@portal/models/FirmwareBuild.js';

class FirmwareBuildView extends View {
    constructor(options = {}) {
        super({
            className: 'firmware-build-view',
            viewDialogOptions: {
                header: false,
                size: 'lg',
            },
            ...options
        });
    }

    async getTemplate() {
        return `
        <div class="p-3">
            <div class="d-flex align-items-center justify-content-between mb-1">
                <h5 class="mb-0"><i class="bi bi-cpu me-2"></i>{{variantLabel}}</h5>
                {{{model.state|badge:pending=warning,published=success,yanked=danger}}}
            </div>
            <div class="text-muted small mb-3">Version {{model.version}} · uploaded {{model.created|datetime}}</div>

            <div class="row g-3 mb-3">
                <div class="col-md-6">
                    <div class="small text-muted">Git Hash</div>
                    <div class="font-monospace small">{{model.git_hash|default('—')}}</div>
                </div>
                <div class="col-md-6">
                    <div class="small text-muted">Uploaded By</div>
                    <div class="small">{{uploadedByLabel}}</div>
                </div>
                <div class="col-md-12">
                    <div class="small text-muted">SHA-256</div>
                    <div class="font-monospace small text-truncate" title="{{model.sha256}}">{{model.sha256|default('—')}}</div>
                </div>
                {{#featureFlagsText|bool}}
                <div class="col-md-12">
                    <div class="small text-muted">Feature Flags</div>
                    <div class="font-monospace small">{{featureFlagsText}}</div>
                </div>
                {{/featureFlagsText|bool}}
                {{#canDownload|bool}}
                <div class="col-md-12">
                    <button type="button" class="btn btn-sm btn-outline-secondary" data-action="download">
                        <i class="bi bi-download me-1"></i>Download {{downloadName}}
                    </button>
                </div>
                {{/canDownload|bool}}
            </div>

            <div class="mb-2 fw-bold small text-uppercase text-muted">Build History</div>
            <div data-container="history-view"></div>

            <div class="d-flex justify-content-end gap-2 mt-3">
                {{#canPublish|bool}}
                <button type="button" class="btn btn-success" data-action="publish">
                    <i class="bi bi-rocket-takeoff me-1"></i>Publish
                </button>
                {{/canPublish|bool}}
                {{#canYank|bool}}
                <button type="button" class="btn btn-outline-danger" data-action="yank">
                    <i class="bi bi-x-octagon me-1"></i>Yank
                </button>
                {{/canYank|bool}}
            </div>
        </div>
        `;
    }

    async onInit() {
        // Row models carry only the list graph — pull the detail graph
        // (signature, feature_flags, nested file + uploaded_by).
        await this.model.fetch();

        this.historyView = new TableView({
            collection: new FirmwareBuildHistoryList({
                params: { build: this.model.get('id'), size: 10 }
            }),
            containerId: 'history-view',
            fetchOnMount: true,
            hideActivePillNames: ['build'],
            clickAction: 'none',
            fontSize: 'sm',
            columns: [
                { key: 'created', label: 'When', formatter: 'datetime' },
                { key: 'action', label: 'Action', formatter: 'badge:published=success,yanked=danger,ingested=secondary,download=info' },
                { key: 'user', label: 'By', formatter: "default('—')" },
                { key: 'metadata.reason', label: 'Reason', formatter: "default('')" },
            ]
        });
        this.addChild(this.historyView);
    }

    async onBeforeRender() {
        this.variantLabel = this.model.variantKey();
        this.canPublish = this.model.isPending();
        this.canYank = this.model.isPublished();

        const flags = this.model.get('feature_flags');
        this.featureFlagsText = flags && Object.keys(flags).length ? JSON.stringify(flags) : '';

        this.canDownload = !!this.getApp().activeUser?.hasPermission('sys.manage_firmware');
        this.downloadName = this.model.get('version') ? `v${this.model.get('version')}` : 'binary';

        const ub = this.model.get('uploaded_by');
        this.uploadedByLabel = (ub && typeof ub === 'object')
            ? (ub.display_name || ub.username || ub.email || `User #${ub.id}`)
            : '—';
    }

    // The build detail route is POST-only on the server — model.save() would
    // PUT and 404, so actions go through app.rest.POST directly.
    async _postAction(payload, successMessage, failMessage) {
        const app = this.getApp();
        const response = await app.rest.POST(`/api/firmware/build/${this.model.get('id')}`, payload);
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || failMessage);
            return false;
        }
        app.toast.success(successMessage);
        await this.model.fetch();
        await this.render();
        return true;
    }

    // The presigned URL is minted per-call and short-lived, so fetch it fresh
    // on click — never precompute or cache. Server: on_action_download returns
    // { status, data: { url, filename, ... } }, so the URL is at
    // response.data.data.url (same envelope FirmwareAssignmentsPage reads).
    async onActionDownload() {
        const app = this.getApp();
        const response = await app.rest.POST(`/api/firmware/build/${this.model.get('id')}`, { download: true });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to fetch download URL');
            return;
        }
        const result = response.data.data || {};
        if (!result.url) {
            app.toast.error('Download unavailable');
            return;
        }
        // Anchor-click, NOT window.open: the fetch resolves after the click
        // gesture, so a popup would be blocked (transient activation gone). A
        // download-anchor to the octet-stream URL is not a popup, and it is the
        // repo idiom (AnalyticsDashboardPage.js, MachineServiceHistoryPanel.js).
        const a = document.createElement('a');
        a.href = result.url;
        if (result.filename) a.download = result.filename;
        a.rel = 'noopener';
        a.click();
        // A `download` history row was written server-side — refresh so it shows.
        this.historyView?.collection?.fetch();
    }

    async onActionPublish() {
        const confirmed = await Dialog.confirm({
            title: 'Publish Build',
            message: `Publish v${this.model.get('version')} to the "${this.model.get('channel')}" channel? Devices tracking this channel become eligible for it.`,
            confirmText: 'Publish',
            confirmClass: 'btn-success',
        });
        if (!confirmed) return;
        await this._postAction({ publish: true }, 'Build published', 'Failed to publish build');
    }

    async onActionYank() {
        const data = await Dialog.showForm({
            title: 'Yank Build',
            fields: [
                {
                    name: 'reason', type: 'textarea', label: 'Reason', columns: 12, required: true,
                    placeholder: 'Why is this build being pulled?',
                },
            ],
        });
        if (!data?.reason) return;
        await this._postAction({ yank: data.reason }, 'Build yanked', 'Failed to yank build');
    }

    static create(options = {}) {
        return new FirmwareBuildView(options);
    }
}

FirmwareBuild.VIEW_CLASS = FirmwareBuildView;
export default FirmwareBuildView;
